import constantURL from "./ConstantURL";
import { postLoginData, getDataAPI } from "./index";
import { parseLockedUntilMs } from "../utils/forgotPasswordResendLock";

export const OTP_PURPOSE_FORGOT_PASSWORD = "FORGOT_PASSWORD";
export const OTP_PURPOSE_EMAIL_VERIFICATION = "EMAIL_VERIFICATION";
export const OTP_ACTION_SEND = "SEND";
export const OTP_ACTION_RESEND = "RESEND";
export const OTP_ROLE_FAMILY_MEMBER = "FAMILYMEMBER";
export const OTP_ROLE_CAREGIVER = "CAREGIVER";
export const INVALID_FAMILYPORTAL_EMAIL_MESSAGE = "Invalid Email Address.";
export const DEFAULT_MAX_OTP_SENDS = 4;

/** Maps sendEmailOTP response fields (attemptCount / attemptsRemaining = send limit). */
export const normalizeSendOtpMeta = (data = {}) => {
  const maxAttempts = Number(data.maxAttempts) || DEFAULT_MAX_OTP_SENDS;
  const attemptCount = Number(data.attemptCount) || 0;
  const attemptsRemaining = data.attemptsRemaining != null ? Number(data.attemptsRemaining) : Math.max(0, maxAttempts - attemptCount);

  return {
    ...data,
    attemptCount,
    attemptsRemaining,
    maxAttempts,
    expiresAt: parseLockedUntilMs(data.expiresAt),
    nextRequestAfter: parseLockedUntilMs(data.nextRequestAfter),
    lockedUntil: parseLockedUntilMs(data.lockedUntil),
    minRequestIntervalSeconds: Number(data.minRequestIntervalSeconds) || 60,
    minutesRemaining: data.minutesRemaining ?? null,
    lockDurationMinutes: data.lockDurationMinutes ?? null,
  };
};

const normalizeOtpResponse = (response) => {
  if (!response) {
    return { success: false, message: "Unexpected server response.", data: null };
  }

  if (response.status === "success") {
    return {
      success: true,
      message: response.message || "Success.",
      data: response.data ?? null,
    };
  }

  return {
    success: false,
    message: response.message || "Request failed.",
    data: response.data ?? null,
  };
};

const postFamilyportalOtp = async (url, payload) => {
  try {
    const response = await postLoginData(url, payload);
    return normalizeOtpResponse(response);
  } catch {
    return { success: false, message: "Unable to reach the server. Please try again.", data: null };
  }
};

export const validateFamilyportalEmail = async (email, { purpose } = {}) => {
  const trimmedEmail = String(email || "")
    .trim()
    .toLowerCase();
  if (!trimmedEmail) {
    return { success: false, message: INVALID_FAMILYPORTAL_EMAIL_MESSAGE, data: null };
  }

  const payload = { email: trimmedEmail };
  if (purpose) {
    payload.purpose = purpose;
  }

  try {
    const response = await postLoginData(constantURL.familyportalValidateEmail, payload);

    if (!response) {
      return { success: false, message: INVALID_FAMILYPORTAL_EMAIL_MESSAGE, data: null };
    }

    if (response.status === "success") {
      return {
        success: true,
        message: response.message || "Email Verified Successfully",
        data: response.data ?? null,
      };
    }

    return {
      success: false,
      message: response.message || INVALID_FAMILYPORTAL_EMAIL_MESSAGE,
      data: response.data ?? null,
    };
  } catch {
    return { success: false, message: INVALID_FAMILYPORTAL_EMAIL_MESSAGE, data: null };
  }
};

export const sendEmailOtp = async ({ email, purpose = OTP_PURPOSE_EMAIL_VERIFICATION, action = OTP_ACTION_SEND, role = OTP_ROLE_FAMILY_MEMBER }) => {
  const trimmedEmail = String(email || "")
    .trim()
    .toLowerCase();
  if (!trimmedEmail) {
    return { success: false, message: "Please enter your email address.", data: null };
  }

  const result = await postFamilyportalOtp(constantURL.familyportalSendEmailOtp, {
    email: trimmedEmail,
    purpose,
    action,
    role: String(role || OTP_ROLE_FAMILY_MEMBER)
      .trim()
      .toUpperCase(),
  });

  if (!result.data) {
    return result;
  }

  return {
    ...result,
    data: normalizeSendOtpMeta(result.data),
  };
};

/**
 * Maps API client rows to app client shape. roomId is the client id.
 */
const normalizeFamilyClient = (client = {}) => {
  const clientId = client.roomId ?? client.clientId ?? client.familyMemberClientId;
  const normalizedClientId = clientId === undefined || clientId === null ? "" : String(clientId);

  return {
    familyId: client.familyId,
    clientId: normalizedClientId,
    familyMemberClientId: normalizedClientId,
    familyClientId: normalizedClientId,
    unit: client.unit || "",
    clientName: client.clientName || client.name || "",
  };
};

let familyClientsCache = {
  familyId: "",
  result: null,
  inflight: null,
};

export const clearFamilyClientsCache = () => {
  familyClientsCache = {
    familyId: "",
    result: null,
    inflight: null,
  };
};

const fetchClientsByFamilyMember = async (familyId) => {
  try {
    const response = await getDataAPI(`${constantURL.getClientByFamilyMember}?familyId=${familyId}`);
    if (!response) {
      return { success: false, message: "Unexpected server response.", data: null };
    }
    if (response.status === "success") {
      const clients = (response.data ?? []).map(normalizeFamilyClient).filter((client) => client.clientId);
      return {
        success: true,
        message: response.message || "Success.",
        data: clients,
      };
    }
    return { success: false, message: response.message || "Failed to fetch clients.", data: null };
  } catch {
    return { success: false, message: "Unable to reach the server. Please try again.", data: null };
  }
};

/**
 * Fetches the list of clients associated with a family member.
 * Results are cached per familyId for the current session to avoid duplicate calls after login.
 * @param {string|number} familyId
 * @returns {Promise<{success: boolean, data: Array|null, message: string}>}
 */
export const getClientsByFamilyMember = async (familyId) => {
  const normalizedFamilyId = String(familyId || "").trim();
  if (!normalizedFamilyId) {
    return { success: false, message: "Family member ID is missing.", data: null };
  }

  if (familyClientsCache.familyId === normalizedFamilyId && familyClientsCache.result) {
    return familyClientsCache.result;
  }

  if (familyClientsCache.familyId === normalizedFamilyId && familyClientsCache.inflight) {
    return familyClientsCache.inflight;
  }

  const request = fetchClientsByFamilyMember(normalizedFamilyId).then((result) => {
    if (result.success) {
      familyClientsCache = {
        familyId: normalizedFamilyId,
        result,
        inflight: null,
      };
    } else {
      familyClientsCache.inflight = null;
    }
    return result;
  });

  familyClientsCache = {
    familyId: normalizedFamilyId,
    result: null,
    inflight: request,
  };

  return request;
};

export const validateEmailOtp = async ({ email, otpCode, purpose = OTP_PURPOSE_EMAIL_VERIFICATION, role = OTP_ROLE_FAMILY_MEMBER }) => {
  const trimmedEmail = String(email || "")
    .trim()
    .toLowerCase();
  const code = String(otpCode || "").replace(/\D/g, "");

  if (!trimmedEmail) {
    return { success: false, message: "Please enter your email address.", data: null };
  }

  if (!code) {
    return { success: false, message: "Please enter the verification code.", data: null };
  }

  return postFamilyportalOtp(constantURL.familyportalValidateEmailOtp, {
    email: trimmedEmail,
    otpCode: code,
    purpose,
    role: String(role || OTP_ROLE_FAMILY_MEMBER)
      .trim()
      .toUpperCase(),
  });
};

export const normalizeMobileForOtpApi = (mobileNumber) => {
  const digits = String(mobileNumber || "").replace(/\D/g, "");
  const localDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (localDigits.length === 10) {
    return `${localDigits.slice(0, 3)}-${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
  }

  return String(mobileNumber || "").trim();
};

export const sendMobileOtp = async ({ mobileNumber, role = OTP_ROLE_FAMILY_MEMBER }) => {
  const normalizedMobile = normalizeMobileForOtpApi(mobileNumber);
  if (!normalizedMobile) {
    return { success: false, message: "Please enter your mobile number.", data: null };
  }

  const result = await postFamilyportalOtp(constantURL.familyportalSendMobileOtp, {
    mobileNumber: normalizedMobile,
    role: String(role || OTP_ROLE_FAMILY_MEMBER)
      .trim()
      .toUpperCase(),
  });

  if (!result.data) {
    return result;
  }

  return {
    ...result,
    data: normalizeSendOtpMeta(result.data),
  };
};

export const validateMobileOtp = async ({ mobileNumber, otpCode, role = OTP_ROLE_FAMILY_MEMBER }) => {
  const normalizedMobile = normalizeMobileForOtpApi(mobileNumber);
  const code = String(otpCode || "").replace(/\D/g, "");

  if (!normalizedMobile) {
    return { success: false, message: "Please enter your mobile number.", data: null };
  }

  if (!code) {
    return { success: false, message: "Please enter the verification code.", data: null };
  }

  return postFamilyportalOtp(constantURL.familyportalValidateMobileOtp, {
    mobileNumber: normalizedMobile,
    otpCode: code,
    role: String(role || OTP_ROLE_FAMILY_MEMBER)
      .trim()
      .toUpperCase(),
  });
};
